import type { AiShieldStats } from '#/services/stats/service.js'
import { STATS_MONTH, STATS_RECENT } from '#/services/stats/spec.js'
import { clear, el } from './dom.js'
import { aiShieldIcon, chevronIcon } from './icons.js'
import { t } from './labels.js'
import { openSheet } from './sheet.js'

// The read-only "AI Shield activity" view — the Mini App face of the shadow-mode signal. Since v9 (user
// call, session 70) it is no longer a standalone page section: the AI Shield HERO panel carries one
// tappable activity row (title + the grand total), and tapping it opens a bottom SHEET with the
// per-category table; each row there drills down to its individual events (ai-shield-flags.ts — that
// sheet REPLACES this one, per the one-sheet-at-a-time contract, and reopens it on close so the ×/Back
// steps one level back to this table, not to the page — main.ts wires that). Split like stats.ts:
// planAiShieldStats() + activityRowValue() are PURE (unit-tested); the row/sheet builders are the thin
// DOM transport (live-verify only). main.ts owns the fetch and its cached state; this module renders
// every face of it (pending / error / loaded), gated on AI Shield being enabled like before.

export interface AiShieldRow { category: string, recent: number, month: number }
export interface AiShieldStatsModel { rows: AiShieldRow[], total: { recent: number, month: number } }

/**
 * PURE: shape an AiShieldStats into display rows (one per category, in the server's canonical order) + the
 * per-window grand total. Returns null when either shared window's total is missing from the response —
 * that's a malformed reply, and the caller renders the loud error state rather than a half-picture (mirror
 * of planStats). `counts[key] ?? 0` is defensive: the server zero-fills, so a present category always has
 * both keys.
 */
export function planAiShieldStats(stats: AiShieldStats): AiShieldStatsModel | null {
  const recentTotal = stats.totals[STATS_RECENT.key]
  const monthTotal = stats.totals[STATS_MONTH.key]
  if (recentTotal === undefined || monthTotal === undefined)
    return null
  const rows = stats.categories.map(c => ({
    category: c.category,
    recent: c.counts[STATS_RECENT.key] ?? 0,
    month: c.counts[STATS_MONTH.key] ?? 0,
  }))
  return { rows, total: { recent: recentTotal, month: monthTotal } }
}

/**
 * The activity load state, owned by main.ts (one fetch per open, cached): `pending` while the one-shot
 * load is in flight, `error` for a failed load OR a malformed reply (planAiShieldStats → null), `ready`
 * with the shaped model otherwise.
 */
export type AiShieldActivityState
  = | { kind: 'pending' }
    | { kind: 'error' }
    | { kind: 'ready', model: AiShieldStatsModel }

/**
 * PURE: the compact readout on the hero panel's activity row — "recent (month)" like every stats pair, an
 * honest "…" while loading, an em dash when the load failed (the sheet behind the row says why, loudly).
 */
export function activityRowValue(state: AiShieldActivityState): string {
  if (state.kind === 'pending')
    return '…'
  if (state.kind === 'error')
    return '—'
  return `${state.model.total.recent} (${state.model.total.month})`
}

/** The drill-down opener a tapped row calls: its category token, or null for the total (all categories). */
export type AiShieldDrill = (category: string | null, triggerEl: HTMLElement) => void

// One "label …… 12 (30)" row — the recent count first, the month count muted in parentheses — the same
// format stats.ts + the in-chat /stats print. Numbers are integers + static labels → safe text nodes (XSS).
// A real <button> (keyboard + AT for free) wearing the shared .row skin plus a chevron — tapping it opens
// the drill-down sheet for the row's slice (its category, or null = the unfiltered total).
function aiShieldRowEl(label: string, recent: number, month: number, drill: { onDrill: AiShieldDrill, category: string | null }): HTMLElement {
  const label_ = el('span', { class: 'label' }, label)
  const value = el('span', { class: 'value' }, `${recent} (${month})`)
  const btn = el('button', { class: 'row row-tap', type: 'button' }, label_, value, chevronIcon('row-chevron'))
  btn.addEventListener('click', () => drill.onDrill(drill.category, btn))
  return btn
}

/**
 * The tappable activity ROW mounted into the AI Shield hero panel's .sec-group (main.ts appends it after
 * the spec-driven rows): title + the grand-total readout + the chevron — the same .row-tap skin as the
 * drill-down rows, so "this opens something" reads the same everywhere. Returns the value span too, so
 * main.ts can repaint the counts in place when the one-shot fetch settles (no full re-render).
 */
export function aiShieldActivityRow(state: AiShieldActivityState, onOpen: (triggerEl: HTMLElement) => void): { row: HTMLElement, value: HTMLElement } {
  const value = el('span', { class: 'value' })
  const row = el('button', { class: 'row row-tap', type: 'button' }, el('span', { class: 'label' }, t('ui-aishield-results-title')), value, chevronIcon('row-chevron'))
  paintActivityRowValue(value, state)
  row.addEventListener('click', () => onOpen(row))
  return { row, value }
}

/** Paint the row's value span from the state — the error dash wears the shared error ink, honestly red. */
export function paintActivityRowValue(value: HTMLElement, state: AiShieldActivityState): void {
  value.textContent = activityRowValue(state)
  value.classList.toggle('stats-error', state.kind === 'error')
}

/**
 * Render the activity sheet's BODY from the load state (replacing `root`'s content — main.ts re-runs this
 * in place if the pending fetch settles while the sheet is open). `ready`: the per-category table — every
 * row tappable via `onDrill` (a category row opens the drill-down sheet filtered to it, the total row the
 * unfiltered list); rows only exist when their counts are non-zero, so every tap has events behind it.
 * Empty state: nothing flagged in the widest window (30d ⊇ 7d, so month 0 ⇒ recent 0 too) — an honest
 * "nothing yet", common until AI Shield is on and flags accumulate. Error: the loud line, in-sheet.
 */
export function renderAiShieldActivity(root: HTMLElement, state: AiShieldActivityState, onDrill: AiShieldDrill): void {
  clear(root)
  if (state.kind === 'pending') {
    root.append(el('p', { class: 'sheet-body' }, '…')) // language-neutral pending, like the drill-down's
    return
  }
  if (state.kind === 'error') {
    root.append(el('p', { class: 'sheet-body stats-error' }, t('ui-aishield-results-error')))
    return
  }
  const model = state.model
  if (model.total.recent === 0 && model.total.month === 0) {
    root.append(el('p', { class: 'sheet-body' }, t('ui-aishield-results-empty')))
    return
  }
  root.append(el('p', { class: 'sheet-body' }, t('ui-aishield-results-caption')))
  const group = el('div', { class: 'sec-group' })
  for (const row of model.rows) {
    if (row.recent === 0 && row.month === 0)
      continue // hide all-zero categories to cut noise (the total row below still reconciles the whole)
    // Dynamic label via a TEMPLATE literal (backtick) — never a quoted-string concat. The codegen's static
    // key scan matches only quote-delimited calls, so a backtick site rides the ai-cat- prefix (like the
    // cfg-opt- options); results-labels.test.ts guards that every category's label exists in every locale.
    group.append(aiShieldRowEl(t(`ai-cat-${row.category}`), row.recent, row.month, { onDrill, category: row.category }))
  }
  group.append(aiShieldRowEl(t('ui-aishield-results-total'), model.total.recent, model.total.month, { onDrill, category: null }))
  root.append(group)
}

/**
 * Open the activity sheet (title + per-category table) over the page. Returns the sheet's body container
 * so main.ts can repaint it via renderAiShieldActivity if the load settles while the sheet is open.
 */
export function openAiShieldActivity(state: AiShieldActivityState, onDrill: AiShieldDrill, triggerEl?: HTMLElement): HTMLElement {
  const body = el('div')
  renderAiShieldActivity(body, state, onDrill)
  openSheet(aiShieldIcon('sheet-icon'), t('ui-aishield-results-title'), [body], { bodyKey: 'ui-aishield-results-help' }, triggerEl)
  return body
}
